'use client';

import { useCallback, useSyncExternalStore } from 'react';

function getMediaQueryList(query: string): MediaQueryList | null {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return null;
    }
    return window.matchMedia(query);
}

/**
 * Whether `query` currently matches. SSR and hydration both report `defaultValue` and the client
 * re-renders after, so only use this where the pre-hydration value is harmless.
 */
export function useMediaQuery(query: string, defaultValue = false): boolean {
    const subscribe = useCallback(
        (onStoreChange: () => void) => {
            const mediaQueryList = getMediaQueryList(query);
            if (!mediaQueryList) {
                return () => undefined;
            }
            mediaQueryList.addEventListener('change', onStoreChange);
            return () => mediaQueryList.removeEventListener('change', onStoreChange);
        },
        [query]
    );

    const getSnapshot = useCallback(() => getMediaQueryList(query)?.matches ?? defaultValue, [defaultValue, query]);

    const getServerSnapshot = useCallback(() => defaultValue, [defaultValue]);

    return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
